import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock } from 'lucide-react';
import { useMudancas } from '@/hooks/useMudancas';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

const MudancaCalendario = () => {
  const { mudancas } = useMudancas();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const mudancasAgendadas = mudancas.filter(m => 
    m.data_execucao_planejada && 
    m.status !== 'cancelada' &&
    m.status !== 'sucesso'
  );

  const getMudancasPorDia = (dia: number) => {
    return mudancasAgendadas.filter(m => {
      if (!m.data_execucao_planejada) return false;
      const data = new Date(m.data_execucao_planejada);
      return data.getDate() === dia && 
             data.getMonth() === selectedMonth && 
             data.getFullYear() === selectedYear;
    });
  };

  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const getRiscoBadgeVariant = (risco?: string) => {
    switch (risco) {
      case 'baixo': return 'default';
      case 'medio': return 'secondary';
      case 'alto': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Calendário de Mudanças
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
            >
              ←
            </Button>
            <span className="text-sm font-medium min-w-[150px] text-center">
              {meses[selectedMonth]} {selectedYear}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
            >
              →
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-2">
          {/* Cabeçalho dos dias da semana */}
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dia => (
            <div key={dia} className="text-center text-sm font-semibold p-2">
              {dia}
            </div>
          ))}

          {/* Espaços vazios antes do primeiro dia */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="p-2" />
          ))}

          {/* Dias do mês */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const dia = i + 1;
            const mudancasDoDia = getMudancasPorDia(dia);
            const hoje = new Date();
            const ehHoje = dia === hoje.getDate() && 
                          selectedMonth === hoje.getMonth() && 
                          selectedYear === hoje.getFullYear();

            return (
              <div 
                key={dia} 
                className={`min-h-[100px] p-2 border rounded-lg ${
                  ehHoje ? 'bg-primary/10 border-primary' : 'bg-background'
                }`}
              >
                <div className="text-sm font-medium mb-1">{dia}</div>
                <div className="space-y-1">
                  {mudancasDoDia.map(mudanca => (
                    <div
                      key={mudanca.id}
                      className="text-xs p-1 rounded bg-muted hover:bg-muted/80 cursor-pointer"
                      title={mudanca.titulo}
                    >
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span className="truncate">
                          {new Date(mudanca.data_execucao_planejada!).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="truncate font-medium">{mudanca.numero}</div>
                      <Badge 
                        variant={getRiscoBadgeVariant(mudanca.classificacao_risco)} 
                        className="text-[10px] h-4 px-1"
                      >
                        {mudanca.tipo_mudanca}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {mudancasAgendadas.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma mudança agendada
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MudancaCalendario;
